import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import './CreateFolderModal.css';

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (folderName: string) => Promise<void>;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({ isOpen, onClose, onCreate }) => {
  const { t } = useLanguage();
  const [folderName, setFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!folderName.trim()) {
      setError(t.folder.enterFolderName);
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      await onCreate(folderName.trim());
      setFolderName('');
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || t.common.error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setFolderName('');
      setError('');
      onClose();
    }
  };

  return (
    <div className="create-folder-modal-overlay" onClick={handleClose}>
      <div className="create-folder-modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{t.folder.createFolder}</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="folder-name">{t.folder.folderName}</label>
            <input
              id="folder-name"
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder={t.folder.enterFolderName}
              autoFocus
              disabled={isCreating}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button 
              type="button" 
              onClick={handleClose}
              disabled={isCreating}
              className="cancel-btn"
            >
              {t.folder.cancel}
            </button>
            <button 
              type="submit"
              disabled={isCreating || !folderName.trim()}
              className="create-btn"
            >
              {isCreating ? t.folder.creating : t.folder.create}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFolderModal;
